"use client";

import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../lib/cn";
import {
	canShareVerticalHoverMenuPanel,
	resolveSharedVerticalHoverMenuNubOffset,
	resolveVerticalHoverMenuPanelGeometry,
} from "./hover-menu-geometry";

export type HoverMenuOrientation = "horizontal" | "vertical";

export type HoverMenuShiftDirection = "l" | "r" | "u" | "d" | null;

export interface HoverMenuItem {
	id: string;
	/** Control rendered in the trigger row/column. */
	trigger: ReactNode;
	/**
	 * Optional per-item panel body. When omitted, {@link HoverMenuProps.panel}
	 * is used (shared group content).
	 */
	content?: ReactNode;
	/** Accessible name for the trigger when `trigger` is not a labelled control. */
	"aria-label"?: string;
	disabled?: boolean;
	/**
	 * Items with the same group id may reuse one panel when the arrow still
	 * fits on that panel’s left edge.
	 */
	shareGroupId?: string;
}

/** Non-interactive chrome between triggers (e.g. section labels). */
export interface HoverMenuSlot {
	kind: "slot";
	id: string;
	node: ReactNode;
}

export type HoverMenuEntry =
	| ({ kind?: "item" } & HoverMenuItem)
	| HoverMenuSlot;

export interface HoverMenuPanelContext {
	selectedId: string;
	direction: HoverMenuShiftDirection;
}

export interface HoverMenuProps {
	/** Prefer {@link entries} when mixing triggers with section slots. */
	items?: readonly HoverMenuItem[];
	entries?: readonly HoverMenuEntry[];
	orientation?: HoverMenuOrientation;
	/** Controlled open item id. */
	value?: string | null;
	defaultValue?: string | null;
	onValueChange?: (value: string | null) => void;
	/**
	 * Shared panel body when an item has no `content`. Receives the active id
	 * so consumers can highlight the hovered mission inside a group list.
	 */
	panel?: ReactNode | ((ctx: HoverMenuPanelContext) => ReactNode);
	className?: string;
	triggersClassName?: string;
	triggerClassName?: string;
	panelClassName?: string;
	/** Invisible hover bridge between triggers and panel (px). @default 16 */
	bridgePx?: number;
	/** Pointer nub aligned to the active trigger. @default true */
	showNub?: boolean;
	/** Also toggle on click (in addition to hover). @default true */
	openOnClick?: boolean;
	/**
	 * Render the panel in a document portal with fixed positioning.
	 * Use when an ancestor clips overflow (e.g. collapsed sidebar rail).
	 */
	portal?: boolean;
	/**
	 * Reuse the open panel when the next trigger’s arrow still fits its left
	 * edge (same {@link HoverMenuItem.shareGroupId}). @default true
	 */
	sharePanelWhenNubFits?: boolean;
	/** Stable id prefix for trigger/panel DOM hooks (tests / a11y). */
	idPrefix?: string;
}

function isHoverMenuSlot(entry: HoverMenuEntry): entry is HoverMenuSlot {
	return entry.kind === "slot";
}

function toHoverMenuItem(entry: HoverMenuEntry): HoverMenuItem | null {
	if (isHoverMenuSlot(entry)) {
		return null;
	}
	return entry;
}

function resolveShiftDirection(
	orientation: HoverMenuOrientation,
	fromId: string,
	toId: string,
	orderedIds: readonly string[],
): HoverMenuShiftDirection {
	const fromIndex = orderedIds.indexOf(fromId);
	const toIndex = orderedIds.indexOf(toId);
	if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
		return null;
	}
	if (orientation === "horizontal") {
		return fromIndex > toIndex ? "r" : "l";
	}
	return fromIndex > toIndex ? "d" : "u";
}

function contentEnterOffset(
	direction: HoverMenuShiftDirection,
): { x?: number; y?: number } {
	if (direction === "l") {
		return { x: 72 };
	}
	if (direction === "r") {
		return { x: -72 };
	}
	if (direction === "u") {
		return { y: 48 };
	}
	if (direction === "d") {
		return { y: -48 };
	}
	return {};
}

/**
 * Shifting hover mega-menu — horizontal (top nav) or vertical (sidebar rail).
 * One singleton panel: shares within a group when the arrow still fits, otherwise
 * smoothly repositions instead of remounting. Mission content always reanimates
 * on selection change.
 */
export function HoverMenu({
	items,
	entries: entriesProp,
	orientation = "horizontal",
	value: valueProp,
	defaultValue = null,
	onValueChange,
	panel,
	className,
	triggersClassName,
	triggerClassName,
	panelClassName,
	bridgePx = 16,
	showNub = true,
	openOnClick = true,
	portal = false,
	sharePanelWhenNubFits = true,
	idPrefix,
}: HoverMenuProps) {
	const reactId = useId();
	const prefix = idPrefix ?? `hover-menu${reactId}`;
	const reduceMotion = useReducedMotion();
	const isControlled = valueProp !== undefined;
	const [uncontrolled, setUncontrolled] = useState<string | null>(
		defaultValue,
	);
	const selected = isControlled ? (valueProp ?? null) : uncontrolled;
	const [direction, setDirection] = useState<HoverMenuShiftDirection>(null);

	const entries = useMemo<HoverMenuEntry[]>(() => {
		if (entriesProp) {
			return [...entriesProp];
		}
		return (items ?? []).map((item) => ({
			...item,
			kind: "item" as const,
		}));
	}, [entriesProp, items]);

	const menuItems = useMemo(
		() =>
			entries
				.map(toHoverMenuItem)
				.filter((item): item is HoverMenuItem => item != null),
		[entries],
	);
	const orderedIds = menuItems.map((item) => item.id);
	const itemById = useMemo(() => {
		const map = new Map<string, HoverMenuItem>();
		for (const item of menuItems) {
			map.set(item.id, item);
		}
		return map;
	}, [menuItems]);

	const rootRef = useRef<HTMLDivElement | null>(null);
	const panelRef = useRef<HTMLDivElement | null>(null);
	const triggerRefs = useRef(new Map<string, HTMLElement>());
	const panelHoverRef = useRef(false);
	const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const panelGeomRef = useRef<{
		top: number;
		left: number;
		height: number;
		shareGroupId: string | null;
	} | null>(null);
	const isPositionedRef = useRef(false);
	const selectedRef = useRef(selected);
	selectedRef.current = selected;

	const [nubOffset, setNubOffset] = useState(0);
	const [panelTop, setPanelTop] = useState(0);
	const [panelLeft, setPanelLeft] = useState(0);
	const [panelHeight, setPanelHeight] = useState(0);
	const [mounted, setMounted] = useState(false);
	const [hasPositioned, setHasPositioned] = useState(false);
	const [allowPositionTransition, setAllowPositionTransition] =
		useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	const setSelected = useCallback(
		(next: string | null) => {
			const prev = selectedRef.current;
			if (
				typeof prev === "string" &&
				typeof next === "string" &&
				prev !== next
			) {
				setDirection(
					resolveShiftDirection(orientation, prev, next, orderedIds),
				);
			} else if (next == null) {
				setDirection(null);
				panelGeomRef.current = null;
				isPositionedRef.current = false;
				setHasPositioned(false);
				setAllowPositionTransition(false);
			}
			if (!isControlled) {
				setUncontrolled(next);
			}
			onValueChange?.(next);
		},
		[isControlled, onValueChange, orderedIds, orientation],
	);

	const clearCloseTimer = useCallback(() => {
		if (closeTimerRef.current != null) {
			clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}
	}, []);

	const scheduleClose = useCallback(() => {
		clearCloseTimer();
		closeTimerRef.current = setTimeout(() => {
			if (!panelHoverRef.current) {
				setSelected(null);
			}
			closeTimerRef.current = null;
		}, 80);
	}, [clearCloseTimer, setSelected]);

	const setTriggerRef = useCallback(
		(id: string, node: HTMLElement | null) => {
			if (node) {
				triggerRefs.current.set(id, node);
			} else {
				triggerRefs.current.delete(id);
			}
		},
		[],
	);

	const updatePanelGeometry = useCallback(() => {
		if (!selected || !rootRef.current) {
			return;
		}
		const trigger = triggerRefs.current.get(selected);
		const panelEl = panelRef.current;
		if (!trigger) {
			return;
		}
		const triggerRect = trigger.getBoundingClientRect();
		const rootRect = rootRef.current.getBoundingClientRect();
		const active = itemById.get(selected);
		const shareGroupId = active?.shareGroupId ?? null;
		const allowPositionTransition = isPositionedRef.current;

		if (portal && orientation === "vertical") {
			const offsetHeight = panelEl?.offsetHeight ?? 0;
			const prev = panelGeomRef.current;
			const heightForShare =
				offsetHeight > 0 ? offsetHeight : (prev?.height ?? 160);
			const heightForPlace = offsetHeight > 0 ? offsetHeight : 160;
			const canShare =
				sharePanelWhenNubFits &&
				prev != null &&
				prev.shareGroupId != null &&
				prev.shareGroupId === shareGroupId &&
				canShareVerticalHoverMenuPanel({
					panelTop: prev.top,
					panelHeight: heightForShare,
					triggerTop: triggerRect.top,
					triggerHeight: triggerRect.height,
				});

			if (canShare && prev) {
				const nub = resolveSharedVerticalHoverMenuNubOffset({
					panelTop: prev.top,
					panelHeight: heightForShare,
					triggerTop: triggerRect.top,
					triggerHeight: triggerRect.height,
				});
				setPanelTop(prev.top);
				setPanelLeft(prev.left);
				setPanelHeight(heightForShare);
				setNubOffset(nub);
				isPositionedRef.current = true;
				setHasPositioned(true);
				setAllowPositionTransition(allowPositionTransition);
				panelGeomRef.current = {
					top: prev.top,
					left: prev.left,
					height: heightForShare,
					shareGroupId,
				};
				return;
			}

			const geometry = resolveVerticalHoverMenuPanelGeometry({
				triggerTop: triggerRect.top,
				triggerHeight: triggerRect.height,
				panelHeight: heightForPlace,
				viewportHeight: window.innerHeight,
				rootRight: rootRect.right,
				bridgePx,
			});
			setPanelTop(geometry.top);
			setPanelLeft(geometry.left);
			setPanelHeight(heightForPlace);
			setNubOffset(geometry.nubOffset);
			isPositionedRef.current = true;
			setHasPositioned(true);
			setAllowPositionTransition(allowPositionTransition);
			panelGeomRef.current = {
				top: geometry.top,
				left: geometry.left,
				height: heightForPlace,
				shareGroupId,
			};
			return;
		}

		if (portal && orientation === "horizontal") {
			setPanelTop(rootRect.bottom + bridgePx);
			setPanelLeft(rootRect.left);
		}

		if (!panelEl) {
			return;
		}
		const panelRect = panelEl.getBoundingClientRect();
		if (orientation === "horizontal") {
			const raw =
				triggerRect.left + triggerRect.width / 2 - panelRect.left;
			const pad = 14;
			setNubOffset(Math.min(panelRect.width - pad, Math.max(pad, raw)));
			return;
		}
		const raw = triggerRect.top + triggerRect.height / 2 - panelRect.top;
		const pad = 14;
		setNubOffset(Math.min(panelRect.height - pad, Math.max(pad, raw)));
	}, [
		bridgePx,
		itemById,
		orientation,
		portal,
		selected,
		sharePanelWhenNubFits,
	]);

	useEffect(() => {
		if (!selected) {
			return;
		}
		const onResize = () => updatePanelGeometry();
		window.addEventListener("resize", onResize);
		window.addEventListener("scroll", onResize, true);
		return () => {
			window.removeEventListener("resize", onResize);
			window.removeEventListener("scroll", onResize, true);
		};
	}, [selected, updatePanelGeometry]);

	useEffect(() => {
		return () => clearCloseTimer();
	}, [clearCloseTimer]);

	const activeItem = selected ? itemById.get(selected) : undefined;

	const panelBody =
		activeItem?.content ??
		(typeof panel === "function"
			? selected
				? panel({ selectedId: selected, direction })
				: null
			: panel);

	useLayoutEffect(() => {
		updatePanelGeometry();
		const panelEl = panelRef.current;
		if (!panelEl || typeof ResizeObserver === "undefined") {
			return;
		}
		const observer = new ResizeObserver(() => {
			updatePanelGeometry();
		});
		observer.observe(panelEl);
		return () => observer.disconnect();
	}, [updatePanelGeometry, selected, panelBody]);

	const isVertical = orientation === "vertical";
	const isOpen = selected != null && panelBody != null;
	const contentEnter = contentEnterOffset(direction);

	const panelNode = (
		<AnimatePresence>
			{isOpen ? (
				<motion.div
					key={`${prefix}-panel-singleton`}
					ref={panelRef}
					id={`${prefix}-panel`}
					role="menu"
					initial={
						reduceMotion
							? { opacity: 0 }
							: {
									opacity: 0,
									...(isVertical ? { x: 8 } : { y: 8 }),
								}
					}
					animate={{ opacity: 1, x: 0, y: 0 }}
					exit={
						reduceMotion
							? { opacity: 0 }
							: {
									opacity: 0,
									...(isVertical ? { x: 8 } : { y: 8 }),
								}
					}
					transition={{ duration: 0.2, ease: "easeInOut" }}
					className={cn(
						"z-[130] rounded-lg border border-border bg-popover p-3 text-popover-foreground shadow-lg",
						!portal && "absolute",
						!portal &&
							(isVertical
								? "left-full top-0"
								: "left-0 top-full"),
						panelClassName,
					)}
					style={
						portal
							? {
									position: "fixed",
									top: panelTop,
									left: panelLeft,
									visibility: hasPositioned
										? "visible"
										: "hidden",
									transition:
										reduceMotion || !allowPositionTransition
											? undefined
											: "top 0.2s ease-in-out, left 0.2s ease-in-out",
								}
							: isVertical
								? { marginLeft: bridgePx }
								: { marginTop: bridgePx }
					}
					onMouseEnter={() => {
						panelHoverRef.current = true;
						clearCloseTimer();
					}}
					onMouseLeave={() => {
						panelHoverRef.current = false;
						scheduleClose();
					}}
					data-testid={`${prefix}-panel`}
				>
					{!portal ? (
						<div
							aria-hidden
							className="absolute"
							style={
								isVertical
									? {
											left: -bridgePx,
											top: 0,
											bottom: 0,
											width: bridgePx,
										}
									: {
											top: -bridgePx,
											left: 0,
											right: 0,
											height: bridgePx,
										}
							}
						/>
					) : (
						<div
							aria-hidden
							className="fixed"
							style={{
								top: panelTop,
								left: panelLeft - bridgePx,
								width: bridgePx,
								height: panelHeight || undefined,
							}}
						/>
					)}
					{showNub ? (
						<motion.span
							aria-hidden
							animate={
								isVertical
									? { top: nubOffset }
									: { left: nubOffset }
							}
							transition={
								reduceMotion
									? { duration: 0 }
									: { duration: 0.2, ease: "easeInOut" }
							}
							className={cn(
								"pointer-events-none absolute h-3.5 w-3.5 rotate-45 rounded-[2px] border border-border bg-popover",
								isVertical
									? "top-0 left-0 -translate-x-1/2 -translate-y-1/2 border-r-0 border-t-0"
									: "left-0 top-0 -translate-x-1/2 -translate-y-1/2 border-b-0 border-r-0",
							)}
						/>
					) : null}
					<div className="relative overflow-hidden">
						<AnimatePresence mode="wait" initial={false}>
							<motion.div
								key={selected}
								initial={
									reduceMotion
										? { opacity: 0 }
										: { opacity: 0, ...contentEnter }
								}
								animate={{ opacity: 1, x: 0, y: 0 }}
								exit={{ opacity: 0 }}
								transition={{
									duration: reduceMotion ? 0 : 0.22,
									ease: "easeInOut",
								}}
							>
								{panelBody}
							</motion.div>
						</AnimatePresence>
					</div>
				</motion.div>
			) : null}
		</AnimatePresence>
	);

	return (
		<div
			ref={rootRef}
			className={cn(
				"relative",
				isVertical ? "flex flex-col" : "flex flex-row",
				className,
			)}
			onMouseLeave={() => {
				if (portal) {
					scheduleClose();
					return;
				}
				setSelected(null);
			}}
			data-orientation={orientation}
			data-testid={prefix}
		>
			<div
				className={cn(
					"relative z-[1] flex",
					isVertical
						? "flex-col items-stretch"
						: "flex-row items-center gap-1",
					triggersClassName,
				)}
				role="menubar"
				aria-orientation={orientation}
			>
				{entries.map((entry) => {
					if (isHoverMenuSlot(entry)) {
						return (
							<div key={entry.id} className="w-full shrink-0">
								{entry.node}
							</div>
						);
					}
					const item = entry;
					const isActive = selected === item.id;
					return (
						<div
							key={item.id}
							id={`${prefix}-trigger-${item.id}`}
							ref={(node) => setTriggerRef(item.id, node)}
							role="menuitem"
							tabIndex={item.disabled ? -1 : 0}
							aria-label={item["aria-label"]}
							aria-expanded={isActive}
							aria-haspopup="menu"
							aria-controls={
								isActive ? `${prefix}-panel` : undefined
							}
							aria-disabled={item.disabled ? true : undefined}
							onMouseEnter={() => {
								clearCloseTimer();
								panelHoverRef.current = false;
								if (!item.disabled) {
									setSelected(item.id);
								}
							}}
							onFocus={() => {
								if (!item.disabled) {
									setSelected(item.id);
								}
							}}
							onClick={() => {
								if (!openOnClick || item.disabled) {
									return;
								}
								setSelected(isActive ? null : item.id);
							}}
							onKeyDown={(event) => {
								if (item.disabled) {
									return;
								}
								if (
									event.key === "Enter" ||
									event.key === " "
								) {
									event.preventDefault();
									setSelected(isActive ? null : item.id);
								}
								if (event.key === "Escape") {
									setSelected(null);
								}
							}}
							className={cn(
								"outline-none",
								"focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
								item.disabled &&
									"cursor-not-allowed opacity-60",
								triggerClassName,
							)}
							data-active={isActive ? "true" : "false"}
							data-share-group={item.shareGroupId}
						>
							{item.trigger}
						</div>
					);
				})}
			</div>

			{portal && mounted && typeof document !== "undefined"
				? createPortal(panelNode, document.body)
				: panelNode}
		</div>
	);
}
